import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { DRUID_STATE_VFX } from './DruidStateVFX';
import {
  ringBlast, shardBurst, glintScatter, groundCloud,
  starFlash, stagedBlast, wispRise, slashArc,
} from './burstBuilders';

// =============================================================================
// DRUID EFFECTS (K21 heat-map, druid slice)
//
// The rename lane authored nature_bolt_impact / wrath_impact /
// moonfire_impact / roots_impact in druid.toml — until now they
// archetype-realized generically AND were shadowed by AbilityVFXMap rows
// playing hit_nature. These are the bespoke reads.
//
// Palette doctrine: druid nature school is #44cc44 (AbilitySchoolColors
// SSOT, echoed by the TOML projectile colors); moonfire is deliberately
// LUNAR — silver-violet, zero green (its #aaffaa projectile tint is the
// projectile's business, not the impact's); roots use the authored
// #6B8E23 aura_color over earth browns. Multi-tone ≥2 fills, one-shot
// ≤600ms per the cookbook contract.
// =============================================================================

/**
 * Nature Bolt Impact — the druid basic (600ms cooldown, so it must stay
 * light and quick). A small green pop shedding leaf-diamond slivers with
 * pale glints. Fast in, fast out — spammable rhythm, no lingering shapes.
 */
const NATURE_BOLT_IMPACT: VFXEffectConfig = {
  id: 'nature_bolt_impact',
  name: 'Nature Bolt Impact',
  category: 'burst',
  description: 'Light green pop shedding leaf-diamond slivers',
  primitives: [
    ringBlast({ radius: 12, color: 0x44CC44, alpha: 0.8, glow: true, depth: 95,
      expandTo: 1.8, durationMs: 220 }),
    // Leaf-diamond slivers — pointed both ends, reads as foliage not shrapnel.
    shardBurst({ count: 5, spread: 20, color: 0x66DD55, alpha: 0.9, depth: 96,
      shard: { points: [0, -8, 3, 0, 0, 6, -3, 0] }, durationMs: 280 }),
    glintScatter({ count: 4, spread: 18, color: 0xCCFFCC, radius: 2, durationMs: 180, delayMs: 40 }),
  ],
};

/**
 * Wrath Impact — the bread-and-butter nuke, so it carries real weight: a
 * staged double ring (spring-green core → deep forest pressure wave), a
 * leaf-sliver burst, and spores drifting UP as the hit settles. The green
 * heavy to nature_bolt's green light.
 */
const WRATH_IMPACT: VFXEffectConfig = {
  id: 'wrath_impact',
  name: 'Wrath Impact',
  category: 'burst',
  description: 'Staged green detonation with leaf burst and rising spores',
  primitives: [
    ...stagedBlast(
      { radius: 18, color: 0x88FF88, alpha: 0.9, glow: true, depth: 96, expandTo: 2.0, durationMs: 240 },
      { radius: 32, color: 0x2E8B2E, alpha: 0.7, depth: 94,
        stroke: { color: 0x88FF88, width: 3 }, expandTo: 2.4, durationMs: 440 },
      90,
    ),
    shardBurst({ count: 7, spread: 30, color: 0x55CC44, alpha: 0.9, glow: true, depth: 95,
      shard: { points: [0, -10, 4, 0, 0, 7, -4, 0] }, travel: 70, durationMs: 360 }),
    // Spores drifting up out of the blast — the living-forest afterbeat.
    wispRise({ count: 6, spread: 22, radius: 3, color: 0xCCFFAA, glow: true,
      travel: 40, durationMs: 520, delayMs: 120, depth: 96 }),
    starFlash({ points: 4, innerRadius: 4, outerRadius: 10, color: 0xEEFFEE, alpha: 0.8, depth: 97, durationMs: 220 }),
  ],
};

/**
 * Moonfire Impact — LUNAR, not leafy. A pale silver moonbeam column drops
 * the light onto the target under a sharp 4-star flash; a violet ring with
 * a lavender stroke carries the arcane-moon tint; silver dust glints. NO
 * green anywhere — the moon owns this hit (guarded by DruidVFX.test.ts).
 */
const MOONFIRE_IMPACT: VFXEffectConfig = {
  id: 'moonfire_impact',
  name: 'Moonfire Impact',
  category: 'burst',
  description: 'Silver moonbeam column, 4-star flash, violet ring — zero green',
  primitives: [
    // The moonbeam — a vertical silver column over the target.
    { type: 'rect', width: 18, height: 90, color: 0xEEEEFF, alpha: 0.55, depth: 94, glow: true,
      offsetY: -45,
      tween: { alpha: 0, scaleX: 0.4, duration: 420, ease: 'Sine.easeOut' } },
    starFlash({ points: 4, innerRadius: 6, outerRadius: 20, color: 0xF5F5FF,
      glow: true, depth: 97, spinRad: Math.PI * 0.25, durationMs: 300 }),
    ringBlast({ radius: 24, color: 0x9977CC, alpha: 0.6, depth: 95,
      stroke: { color: 0xC8A2E8, width: 2 }, expandTo: 2.2, durationMs: 420 }),
    // Silver moon-dust settling.
    glintScatter({ count: 6, spread: 26, color: 0xE6E6FA, radius: 2, durationMs: 260, delayMs: 100 }),
  ],
};

/**
 * Roots Impact — the ground grabs. An earthy cloud bursts at the feet
 * (authored #6B8E23 aura over soil brown) while two vine arcs whip UP out
 * of it (slashArc + rising y drift — vines climbing, not blades swinging),
 * trailed by small moss motes. Low, grounded, grasping.
 */
const ROOTS_IMPACT: VFXEffectConfig = {
  id: 'roots_impact',
  name: 'Roots Impact',
  category: 'burst',
  description: 'Earthy ground burst with vine arcs whipping upward',
  primitives: [
    groundCloud({ width: 70, height: 18, color: 0x4A3B1E, alpha: 0.5, depth: 92,
      offsetY: 16, stroke: { color: 0x6B8E23, width: 2 }, swellTo: 1.4, durationMs: 560 }),
    // Vine arc — left whip, rising as it fades (builder-derived y drift).
    (() => {
      const vine = slashArc({ radius: 26, startDeg: 200, endDeg: 320,
        color: 0x6B8E23, width: 5, alpha: 0.9, glow: true, durationMs: 420, depth: 95 });
      vine.tween = { ...vine.tween!, y: -18 };
      return vine;
    })(),
    // Vine arc — right whip, a beat later, brighter green.
    (() => {
      const vine = slashArc({ radius: 22, startDeg: -160, endDeg: -40,
        color: 0x44CC44, width: 4, alpha: 0.85, durationMs: 400, fadeDelayMs: 60, depth: 96 });
      vine.tween = { ...vine.tween!, y: -14 };
      return vine;
    })(),
    // Moss motes shaken loose, drifting up with the vines.
    wispRise({ count: 5, spread: 18, radius: 2.5, color: 0x8FBC5A,
      travel: 26, durationMs: 480, delayMs: 80, depth: 94 }),
  ],
};

// =============================================================================
// EXPORTS
// =============================================================================

export const DRUID_VFX: VFXEffectConfig[] = [
  NATURE_BOLT_IMPACT,
  WRATH_IMPACT,
  MOONFIRE_IMPACT,
  ROOTS_IMPACT,
  ...DRUID_STATE_VFX,
];
